import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { fErrors } from 'utils/formatError';
import { useNotification } from 'hook/useNotification';
import { NOT_FOUND } from 'utils/messageUtils';

const useMetadata = (useGetMetadataQuery, type) => {
  const { formatMessage: msg } = useIntl();
  const { addNotification } = useNotification();

  const [loading, setLoading] = useState(true);
  const [metadata, setMetadata] = useState([]);

  const queryResponse = useGetMetadataQuery();

  useEffect(() => {
    setLoading(queryResponse.isLoading || queryResponse.isFetching);
    setMetadata(queryResponse.data);
    if (queryResponse.error) {
      addNotification({
        type: 'error',
        title: msg({ id: 'notification.error.title' }),
        message: fErrors(msg, queryResponse.error)
      });
    }
  }, [
    addNotification,
    msg,
    queryResponse.data,
    queryResponse.error,
    queryResponse.isFetching,
    queryResponse.isLoading
  ]);

  const getTitle = useCallback(
    (code, plural) => {
      if (!code) {
        return '';
      }
      code = code.substring(code.lastIndexOf('.') + 1);
      // Search if key is defined in messages.json
      let title = msg({
        id: `metadata.${type}.${code}${plural ? '.plural' : ''}`,
        defaultMessage: NOT_FOUND
      });
      if (title === NOT_FOUND) {
        title = metadata?.find((x) => x.code === code)?.name;
      }
      if (!title) {
        title = code;
      }
      return title;
    },
    [metadata, msg, type]
  );

  const getPropertyType = useCallback((code) => metadata?.find((x) => x.code === code), [metadata]);

  const getQueryablePropertyTypes = useMemo(
    () => metadata?.filter((x) => x.queryable === true),
    [metadata]
  );

  const getOrderablePropertyTypes = useMemo(
    () => metadata?.filter((x) => x.orderable === true),
    [metadata]
  );

  return {
    metadata,
    loading,
    error: queryResponse?.error,
    getTitle,
    getPropertyType,
    getQueryablePropertyTypes,
    getOrderablePropertyTypes
  };
};

export default useMetadata;
